import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api } from '../api';
import { discountPct, formatMoney, relativeTime } from '../lib/format';
import { getUserId } from '../lib/userId';

type Variation = {
  delta: number;
  pct: number;
  direction: 'down' | 'up' | 'flat';
};

function computeVariation(start: number | null, current: number | null): Variation | null {
  if (start == null || current == null || start <= 0) return null;
  const delta = current - start;
  if (delta === 0) return { delta: 0, pct: 0, direction: 'flat' };
  return {
    delta,
    pct: Math.round((Math.abs(delta) / start) * 100),
    direction: delta < 0 ? 'down' : 'up',
  };
}

export function Watchlist() {
  const userId = getUserId();

  const items = useQuery({
    queryKey: ['watchlist', userId],
    queryFn: () => api.watchlist(userId),
    staleTime: 60_000,
  });

  return (
    <>
      <div className="toolbar">
        <span style={{ color: 'var(--text-dim)', fontSize: 12 }}>
          Productos que estás siguiendo — te aparecerán aquí con su precio actualizado.
        </span>
      </div>

      {items.isLoading && <div className="loading">Cargando…</div>}
      {items.isError && (
        <div className="error">Error: {(items.error as Error).message}</div>
      )}
      {items.data && items.data.length === 0 && (
        <div className="empty">
          Todavía no seguís ningún producto. Abrí un producto y hacé clic en "Seguir".
        </div>
      )}

      <div className="grid">
        {items.data?.map((item) => {
          const pct = discountPct(item.current_price_cents, item.list_price_cents);
          const variation = computeVariation(item.start_price_cents, item.current_price_cents);
          return (
            <Link key={item.id} to={`/p/${item.product_id}`} className="card">
              {item.image_url ? (
                <img className="card-img" src={item.image_url} alt="" loading="lazy" />
              ) : (
                <div className="card-img-fallback">sin imagen</div>
              )}
              <div className="card-name" title={item.name}>
                {item.name}
              </div>
              <div className="card-price-row">
                <span className="card-price">{formatMoney(item.current_price_cents)}</span>
                {item.list_price_cents && item.list_price_cents !== item.current_price_cents && (
                  <span className="card-list">{formatMoney(item.list_price_cents)}</span>
                )}
                {pct != null && <span className="badge badge-discount">-{pct}%</span>}
              </div>
              <VariationRow
                variation={variation}
                startPriceCents={item.start_price_cents}
                createdAt={item.created_at}
              />
              <div className="card-foot">
                <span>{item.store_id ?? ''}</span>
                {item.brand && <span>{item.brand}</span>}
              </div>
              <div className="card-seen">
                {item.priced_at ? `Actualizado ${relativeTime(item.priced_at)}` : 'Sin precio aún'}
              </div>
            </Link>
          );
        })}
      </div>
    </>
  );
}

function VariationRow({
  variation,
  startPriceCents,
  createdAt,
}: {
  variation: Variation | null;
  startPriceCents: number | null;
  createdAt: string;
}) {
  if (!variation) {
    return (
      <div className="variation variation-flat" title="Sin snapshot posterior al follow">
        Seguís desde {relativeTime(createdAt)} · sin variación aún
      </div>
    );
  }
  if (variation.direction === 'flat') {
    return (
      <div className="variation variation-flat">
        Igual desde que seguís (desde {formatMoney(startPriceCents)})
      </div>
    );
  }
  const arrow = variation.direction === 'down' ? '↓' : '↑';
  const verb = variation.direction === 'down' ? 'Bajó' : 'Subió';
  return (
    <div
      className={`variation variation-${variation.direction}`}
      title={`Precio cuando lo agregaste: ${formatMoney(startPriceCents)}`}
    >
      <span className="variation-arrow">{arrow}</span>
      {verb} {formatMoney(Math.abs(variation.delta))} ({variation.pct}%) desde que seguís
    </div>
  );
}
